import { useEffect, useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

export function useSWAutoUpdate() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      // registration.update() fetches the SW script to check for a new
      // version. It can reject with "An unknown error occurred when
      // fetching the script" for transient reasons (CDN hiccup, identical
      // bytes, proxy caching) that don't indicate a real problem — the
      // next visibilitychange will retry. Swallow the rejection so it
      // doesn't pollute error telemetry.
      registration?.update().catch((err) => {
        // eslint-disable-next-line no-console -- deliberately the quiet debug channel: warn/error would pollute telemetry for a benign, self-retrying failure (see comment above)
        console.debug('Initial SW update check failed:', err)
      })
    },
  })

  const updateRef = useRef(updateServiceWorker)
  updateRef.current = updateServiceWorker

  const needRefreshRef = useRef(needRefresh)
  needRefreshRef.current = needRefresh

  // Apply update when the app resumes from background (feels like a fresh open)
  // Also check for updates each time the app comes to the foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (needRefreshRef.current) {
          updateRef.current(true)
        } else {
          // No update waiting yet — ask the SW to check now so it's
          // ready by the next background→foreground cycle. Same caveat
          // as the initial registration: update() can reject transiently
          // and we shouldn't turn that into an unhandled rejection.
          navigator.serviceWorker
            .getRegistration()
            .then((r) => r?.update())
            .catch((err) => {
              // eslint-disable-next-line no-console -- deliberately the quiet debug channel (same rationale as the initial check above)
              console.debug('Foreground SW update check failed:', err)
            })
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])
}
